import AddIcon from '@mui/icons-material/Add'
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Stack,
  Typography,
} from '@mui/material'
import React from 'react'
import { useIntl } from 'react-intl'
import { sortedOptionsFor, unlocksAtTier } from '../../shared/layer-stack'
import type { BoOption, BoSection } from '../../shared/types'
import AddTile from './AddTile'
import EmptySectionHelp from './EmptySectionHelp'
import type { OptionAction } from './OptionActionsMenu'
import OptionCard from './OptionCard'
import OptionGridSkeleton from './OptionGridSkeleton'
import SwatchGrid, { type SwatchAction } from './SwatchGrid'

export interface OptionGridProps {
  section?: BoSection
  options: BoOption[]
  loading?: boolean
  showRetired: boolean
  onShowRetiredChange: (next: boolean) => void
  onOptionAction?: (option: BoOption, action: OptionAction) => void
  onAddOption?: () => void
  onTryOn?: () => void
  onReorderSwatches?: (
    swatches: string[],
    moved: string,
    direction: 'earlier' | 'later',
  ) => void
  onSwatchAction?: (hex: string, action: SwatchAction) => void
}

export default function OptionGrid({
  section,
  options,
  loading = false,
  showRetired,
  onShowRetiredChange,
  onOptionAction,
  onAddOption,
  onTryOn,
  onReorderSwatches,
  onSwatchAction,
}: OptionGridProps) {
  const { formatMessage } = useIntl()

  if (loading) {
    return <OptionGridSkeleton />
  }
  if (!section) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
        {formatMessage({ id: 'avatarStudio.grid.pickASection' })}
      </Typography>
    )
  }

  // A colour section has no options at all: it holds hex swatches. Falling through to the option
  // path showed "0 options" and taught PNG file naming that can never apply here.
  const isColour = section.kind === 'color'
  const inSection = sortedOptionsFor(options, section.id) as BoOption[]
  // Colour sections count withdrawn SWATCHES; option sections count retired options. One
  // checkbox reveals whichever this section has.
  const retiredCount = isColour
    ? (section.retiredSwatches ?? []).length
    : inSection.filter((o) => o.status === 'retired').length
  const visible = showRetired
    ? inSection
    : inSection.filter((o) => o.status !== 'retired')

  return (
    <Stack spacing={2} sx={{ p: 2 }}>
      <Stack spacing={1}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          flexWrap="wrap"
          sx={{ gap: 1 }}
        >
          <Typography variant="h6" component="h2">
            {section.displayName ?? section.id}
          </Typography>
          <Stack direction="row" sx={{ gap: 1 }} flexWrap="wrap">
            {onAddOption && (
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={onAddOption}
                data-testid="section-add"
              >
                {formatMessage({
                  id: isColour
                    ? 'avatarStudio.grid.addColour'
                    : 'avatarStudio.wizard.addOption',
                })}
              </Button>
            )}
            {onTryOn && (
              <Button
                size="small"
                startIcon={<VisibilityOutlinedIcon />}
                onClick={onTryOn}
              >
                {formatMessage({ id: 'avatarStudio.preview.tryOn' })}
              </Button>
            )}
          </Stack>
        </Stack>
        {retiredCount > 0 && (
          <FormControlLabel
            sx={{ alignSelf: 'flex-start' }}
            control={
              <Checkbox
                size="small"
                checked={showRetired}
                onChange={(e) => onShowRetiredChange(e.target.checked)}
              />
            }
            label={formatMessage(
              {
                id: isColour
                  ? 'avatarStudio.grid.showRetiredColours'
                  : 'avatarStudio.grid.showRetired',
              },
              { count: retiredCount },
            )}
          />
        )}
      </Stack>

      {isColour ? (
        <SwatchGrid
          section={section}
          showRetired={showRetired}
          onAdd={onAddOption}
          onReorder={onReorderSwatches}
          onSwatchAction={onSwatchAction}
        />
      ) : inSection.length === 0 ? (
        <EmptySectionHelp />
      ) : (
        <Box
          sx={{
            display: 'grid',
            gap: 2,
            gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
          }}
        >
          {visible.map((option) => (
            <OptionCard
              key={option.slug}
              option={option}
              unlocksAt={unlocksAtTier(
                section.tierRule,
                inSection.indexOf(option),
                option.slug,
              )}
              canMoveEarlier={inSection.indexOf(option) > 0}
              canMoveLater={inSection.indexOf(option) < inSection.length - 1}
              onAction={
                onOptionAction
                  ? (action) => onOptionAction(option, action)
                  : undefined
              }
            />
          ))}

          {onAddOption && (
            <AddTile
              label={formatMessage({ id: 'avatarStudio.wizard.addOption' })}
              onClick={onAddOption}
            />
          )}
        </Box>
      )}
    </Stack>
  )
}
